<template>
  <div class="flex flex-col w-full h-full overflow-y-auto px-4 pb-8 gap-4">
    <!-- STEP 1: Consent -->
    <div v-if="step === 'consent'" class="flex flex-col gap-4 pt-4">
      <div class="flex flex-col items-center text-center gap-2">
        <div class="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
          <i class="pi pi-camera text-primary text-2xl" />
        </div>
        <h2 class="text-lg font-semibold">סריקת עור חדשה</h2>
        <p class="text-sm text-gray-500 max-w-xs">
          נצלם את פני הלקוח/ה ונבצע ניתוח עור מבוסס בינה מלאכותית למעקב טיפולי לאורך זמן.
        </p>
      </div>

      <label class="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer">
        <input type="checkbox" v-model="consent" class="mt-1 w-4 h-4 accent-primary" />
        <span class="text-sm text-gray-700 leading-relaxed">
          הלקוח/ה מאשר/ת את צילום פניו/ה ואת שמירת התמונה ותוצאות הסריקה לצורך מעקב טיפולי.
        </span>
      </label>

      <p class="text-[11px] text-gray-400 leading-relaxed">
        * הסריקה היא הערכה אסתטית בלבד ואינה מהווה אבחנה רפואית. התמונה נשמרת באופן מאובטח ומשויכת ללקוח/ה בלבד.
      </p>

      <button
        type="button"
        class="w-full py-3 rounded-full bg-primary text-white font-semibold disabled:opacity-50"
        :disabled="!consent"
        @click="goToSlots"
      >
        המשך לצילום
      </button>
    </div>

    <!-- STEP 2: Slots (one per requested direction) -->
    <div v-else-if="step === 'slots'" class="flex flex-col gap-3 pt-2">
      <p class="text-sm text-gray-500 text-center">
        צלמו את הזוויות המבוקשות. ניתן לצלם כמה תמונות בסריקה אחת.
      </p>

      <div class="grid grid-cols-3 gap-2.5">
        <button
          v-for="dir in directions"
          :key="dir"
          type="button"
          class="relative flex flex-col items-center justify-center gap-1.5 rounded-xl border aspect-[3/4] overflow-hidden transition-colors"
          :class="captured[dir] ? 'border-primary' : 'border-dashed border-gray-300 bg-gray-50'"
          @click="openCaptureFor(dir)"
        >
          <template v-if="captured[dir]">
            <img :src="captured[dir]!.url" alt="" class="absolute inset-0 w-full h-full object-cover" />
            <span class="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[11px] py-0.5 text-center">
              {{ directionLabels[dir] }}
            </span>
            <span v-if="captured[dir]!.poor" class="absolute top-1 right-1 bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center" title="איכות נמוכה">
              <i class="pi pi-exclamation-triangle" style="font-size: 0.6rem" />
            </span>
            <span class="absolute top-1 left-1 bg-white/90 text-gray-700 rounded-full w-5 h-5 flex items-center justify-center">
              <i class="pi pi-refresh" style="font-size: 0.6rem" />
            </span>
          </template>
          <template v-else>
            <i class="pi pi-camera text-gray-400 text-xl" />
            <span class="text-xs text-gray-500">{{ directionLabels[dir] }}</span>
          </template>
        </button>
      </div>

      <button
        type="button"
        class="w-full py-3 rounded-full bg-primary text-white font-semibold disabled:opacity-50"
        :disabled="!hasFront"
        @click="submitAll"
      >
        {{ capturedCount > 1 ? `שליחה (${capturedCount} תמונות)` : 'שליחה לניתוח' }}
      </button>
      <p v-if="!hasFront" class="text-[11px] text-gray-400 text-center">יש לצלם לפחות את תמונת החזית.</p>
    </div>

    <!-- STEP 3: Capture a single direction -->
    <div v-else-if="step === 'capture'" class="flex flex-col gap-3 pt-2">
      <div class="flex items-center gap-2">
        <button type="button" class="text-gray-500" @click="backToSlots"><i class="pi pi-arrow-right" /></button>
        <span class="text-sm font-medium">צילום {{ directionLabels[captureDirection] }}</span>
      </div>

      <!-- Review a just-captured shot (always allow "use anyway") -->
      <div v-if="pending" class="flex flex-col gap-3">
        <div class="relative w-full rounded-2xl overflow-hidden bg-black" style="aspect-ratio: 3 / 4;">
          <img :src="pending.url" alt="" class="w-full h-full object-cover" />
        </div>
        <div v-if="pending.poor" class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
          <i class="pi pi-exclamation-triangle text-amber-500 mt-0.5" style="font-size: 0.8rem" />
          <span class="text-xs text-amber-700">{{ pending.guidance || 'איכות התמונה נמוכה' }} — אפשר לשלוח בכל זאת.</span>
        </div>
        <div class="flex gap-2">
          <button type="button" class="flex-1 py-2.5 rounded-full border border-gray-300 text-gray-700 text-sm" @click="retake">
            צילום מחדש
          </button>
          <button type="button" class="flex-1 py-2.5 rounded-full bg-primary text-white text-sm font-medium" @click="acceptPending">
            {{ pending.poor ? 'שליחה בכל זאת' : 'שימוש בתמונה' }}
          </button>
        </div>
      </div>

      <template v-else>
        <!-- Live camera -->
        <div v-if="cameraActive" class="relative w-full rounded-2xl overflow-hidden bg-black" style="aspect-ratio: 3 / 4;">
          <video ref="videoRef" autoplay playsinline muted class="w-full h-full object-cover" />

          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              class="rounded-[50%] border-2 transition-colors duration-300"
              :class="ready ? 'border-green-400' : 'border-white/70'"
              style="width: 58%; height: 72%;"
            />
          </div>

          <div class="absolute top-3 inset-x-0 flex justify-center px-4 pointer-events-none">
            <div
              class="px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur flex items-center gap-1.5"
              :class="ready ? 'bg-green-500/90 text-white' : 'bg-black/60 text-white'"
            >
              <i :class="ready ? 'pi pi-check-circle' : 'pi pi-info-circle'" style="font-size: 0.7rem" />
              <span>{{ guidance }}</span>
            </div>
          </div>

          <div class="absolute bottom-4 inset-x-0 flex items-center justify-center pointer-events-auto">
            <button
              type="button"
              class="w-16 h-16 rounded-full border-4 flex items-center justify-center transition-transform active:scale-95"
              :class="ready ? 'border-green-400 bg-white' : 'border-white bg-white/90'"
              @click="captureFromVideo"
            >
              <span class="w-12 h-12 rounded-full" :class="ready ? 'bg-green-400' : 'bg-primary'" />
            </button>
          </div>
        </div>

        <!-- Camera unavailable -->
        <div v-else class="flex flex-col items-center justify-center text-center gap-3 py-8 px-4 rounded-2xl bg-gray-50 border border-dashed border-gray-300">
          <i class="pi pi-video text-3xl text-gray-400" />
          <p class="text-sm text-gray-500">{{ cameraError || 'המצלמה אינה זמינה' }}</p>
        </div>

        <!-- Fallback: pick a photo -->
        <button
          type="button"
          class="w-full py-2.5 rounded-full border border-gray-300 text-gray-700 text-sm flex items-center justify-center gap-2"
          @click="pickPhoto"
        >
          <i class="pi pi-image text-xs" />
          <span>בחירת תמונה מהגלריה</span>
        </button>

        <input
          v-if="!isNative"
          ref="fileInputRef"
          type="file"
          class="hidden"
          accept="image/png, image/jpeg, image/webp"
          @change="onFileSelected"
        />
      </template>
    </div>

    <!-- STEP 4: Analyzing -->
    <div v-else-if="step === 'analyzing'" class="flex flex-col items-center justify-center gap-4 py-16">
      <i class="pi pi-spin pi-spinner text-primary" style="font-size: 2.5rem" />
      <p class="text-sm text-gray-600">מנתח את העור...</p>
      <p class="text-xs text-gray-400">זה ייקח כמה שניות</p>
    </div>

    <!-- STEP 5: Result (inline — same drawer) -->
    <div v-else-if="step === 'result' && resultScan" class="-mx-4">
      <SkinScanResult :scan="resultScan" @deleted="emit('changed')" @close="emit('close')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useToast } from 'vue-toastification';
import type { ISkinScan } from '@torim-app/types';
import {
  SkinImageDirectionEnum,
  SkinScanSourceEnum,
  skinImageDirectionLabels,
  skinImageDirectionOrder,
  defaultSkinScanDirections,
} from '@torim-app/types';
import { SkinScanApi } from '@app/api/admin';
import { useImageQuality } from '@app/composables/useImageQuality';
import SkinScanResult from './SkinScanResult.vue';

const props = defineProps<{
  customerId: string;
  appointmentId?: string;
  treatmentSessionId?: string;
}>();
const emit = defineEmits<{ (e: 'scanned', scan: ISkinScan): void; (e: 'changed'): void; (e: 'close'): void }>();

const toast = useToast();
const { analyzeSource } = useImageQuality();

type Step = 'consent' | 'slots' | 'capture' | 'analyzing' | 'result';
const step = ref<Step>('consent');
const consent = ref(false);
const resultScan = ref<ISkinScan | null>(null);

const directionLabels = skinImageDirectionLabels;
const directions = ref<SkinImageDirectionEnum[]>([...defaultSkinScanDirections]);

interface CapturedImage { blob: Blob; url: string; poor: boolean; guidance?: string }
const captured = reactive<Partial<Record<SkinImageDirectionEnum, CapturedImage>>>({});
const captureDirection = ref<SkinImageDirectionEnum>(SkinImageDirectionEnum.Front);
const pending = ref<CapturedImage | null>(null);

const capturedCount = computed(() => Object.values(captured).filter(Boolean).length);
const hasFront = computed(() => !!captured[SkinImageDirectionEnum.Front]);

const videoRef = ref<HTMLVideoElement>();
const fileInputRef = ref<HTMLInputElement>();
const cameraActive = ref(false);
const cameraError = ref('');
const ready = ref(false);
const guidance = ref('מתחילים...');

const isNative = Capacitor.isNativePlatform();

let stream: MediaStream | null = null;
let qualityTimer: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
  try {
    const { directions: dirs } = await SkinScanApi.getRequestedDirections(props.customerId);
    if (dirs?.length) directions.value = skinImageDirectionOrder.filter(d => dirs.includes(d));
  } catch {
    // keep the default (front only)
  }
});

const goToSlots = () => {
  step.value = 'slots';
};

const openCaptureFor = async (dir: SkinImageDirectionEnum) => {
  captureDirection.value = dir;
  clearPending();
  step.value = 'capture';
  await startCamera();
};

const backToSlots = () => {
  stopCamera();
  clearPending();
  step.value = 'slots';
};

const startCamera = async () => {
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
      audio: false,
    });
    cameraActive.value = true;
    await new Promise(r => setTimeout(r, 50));
    if (videoRef.value) {
      videoRef.value.srcObject = stream;
      await videoRef.value.play().catch(() => undefined);
      startQualityLoop();
    }
  } catch (e) {
    console.warn('Camera unavailable', e);
    cameraActive.value = false;
    cameraError.value = 'לא ניתן לגשת למצלמה. ניתן לבחור תמונה מהגלריה.';
  }
};

const startQualityLoop = () => {
  stopQualityLoop();
  qualityTimer = setInterval(async () => {
    const video = videoRef.value;
    if (!video || video.readyState < 2) return;
    const result = await analyzeSource(video);
    ready.value = result.usable;
    guidance.value = result.guidance || (result.usable ? 'מצוין! אפשר לצלם' : '');
  }, 600);
};

const stopQualityLoop = () => {
  if (qualityTimer) {
    clearInterval(qualityTimer);
    qualityTimer = null;
  }
};

const stopCamera = () => {
  stopQualityLoop();
  if (stream) {
    stream.getTracks().forEach(t => t.stop());
    stream = null;
  }
  cameraActive.value = false;
};

const captureFromVideo = async () => {
  const video = videoRef.value;
  if (!video || !video.videoWidth) return;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) return;
  // Use the live-quality verdict as the soft check (never blocks).
  setPending(blob, !ready.value, guidance.value);
  stopCamera();
};

const pickPhoto = async () => {
  if (isNative) {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
      });
      if (photo?.webPath) {
        const blob = await fetch(photo.webPath).then(r => r.blob());
        stopCamera();
        await reviewBlob(blob);
      }
    } catch {
      // user cancelled — ignore
    }
  } else {
    fileInputRef.value?.click();
  }
};

const onFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  target.value = '';
  if (file) {
    stopCamera();
    await reviewBlob(file);
  }
};

/** Run a soft quality check on a picked image (poor => warn, never block). */
const reviewBlob = async (blob: Blob) => {
  let poor = false;
  let guide = '';
  try {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
    const result = await analyzeSource(img);
    poor = !result.usable;
    guide = result.guidance || '';
    URL.revokeObjectURL(url);
  } catch {
    // if we can't assess it, accept it silently
  }
  setPending(blob, poor, guide);
};

const setPending = (blob: Blob, poor: boolean, guide?: string) => {
  clearPending();
  pending.value = { blob, url: URL.createObjectURL(blob), poor, guidance: guide };
};

const clearPending = () => {
  if (pending.value) {
    URL.revokeObjectURL(pending.value.url);
    pending.value = null;
  }
};

const retake = async () => {
  clearPending();
  await startCamera();
};

const acceptPending = () => {
  if (!pending.value) return;
  const dir = captureDirection.value;
  // Replace any existing capture for this direction.
  const prev = captured[dir];
  if (prev) URL.revokeObjectURL(prev.url);
  captured[dir] = pending.value;
  pending.value = null; // ownership transferred to `captured` (do not revoke its url)
  step.value = 'slots';
};

const submitAll = async () => {
  if (!hasFront.value) return;
  step.value = 'analyzing';
  try {
    const formData = new FormData();
    const orderedDirs = directions.value.filter(d => captured[d]);
    orderedDirs.forEach(dir => {
      formData.append('images', captured[dir]!.blob, `skin-scan-${dir}.jpg`);
    });
    formData.append('directions', JSON.stringify(orderedDirs));
    formData.append('customerId', props.customerId);
    formData.append('consentAcknowledged', 'true');
    formData.append('source', SkinScanSourceEnum.Clinic);
    if (props.appointmentId) formData.append('appointmentId', props.appointmentId);
    if (props.treatmentSessionId) formData.append('treatmentSessionId', props.treatmentSessionId);

    const scan = await SkinScanApi.createScan(formData);
    toast.success('הסריקה הושלמה!');
    resultScan.value = scan;
    emit('scanned', scan);
    step.value = 'result';
  } catch (e) {
    console.error(e);
    toast.error((e as Error)?.message || 'הסריקה נכשלה, נסו שוב');
    step.value = 'slots';
  }
};

onBeforeUnmount(() => {
  stopCamera();
  clearPending();
  Object.values(captured).forEach(c => c && URL.revokeObjectURL(c.url));
});
</script>
